import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ChevronDown, ChevronRight, Package, ShoppingBag, AlertCircle, IndianRupee, Download } from "lucide-react";

const GROUP_ORDER = ["ps5_controllers", "ps5_games", "ps4_games", "switch1_games", "switch2_games"];

export default function AdminSummary() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/products/export-csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "products_inventory.csv";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/admin/summary"),
      api.get("/admin/stats"),
    ]).then(([summaryRes, statsRes]) => {
      setData(summaryRes.data);
      setStats(statsRes.data);
      setLoading(false);
    });
  }, []);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const statCards = [
    { i: Package, t: "Products", v: stats?.products ?? "—", c: "text-sky-400" },
    { i: ShoppingBag, t: "Total Orders", v: stats?.orders ?? "—", c: "text-emerald-400" },
    { i: AlertCircle, t: "Pending Orders", v: stats?.pending ?? "—", c: "text-rose-400" },
    { i: IndianRupee, t: "Revenue", v: stats?.revenue !== undefined ? `₹${Number(stats.revenue).toLocaleString()}` : "—", c: "text-gold" },
  ];

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Overview</div>
          <h1 className="text-3xl font-black uppercase tracking-tighter">Summary</h1>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-gold text-navy-900 rounded-lg font-bold text-sm uppercase tracking-wide hover:bg-gold/90 disabled:opacity-50 transition"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {statCards.map((c, i) => (
          <div key={i} className="bg-navy-700 rounded-xl p-6 neon-border">
            <c.i className={`w-6 h-6 ${c.c} mb-3`} />
            <div className="text-xs uppercase tracking-wider text-slate-400">{c.t}</div>
            <div className="text-2xl font-black mt-1" data-testid={`stat-${c.t.toLowerCase().replace(/\s/g, '-')}`}>{c.v}</div>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Inventory</div>
        <h2 className="text-xl font-black uppercase tracking-tighter">Stock Summary</h2>
      </div>

      {loading && (
        <div className="text-slate-400 text-sm">Loading...</div>
      )}

      {data && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {GROUP_ORDER.map((key) => {
              const g = data[key];
              if (!g) return null;
              return (
                <div key={key} className="bg-navy-700 rounded-xl neon-border p-4 text-center">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-gold/70 mb-1">{g.label}</div>
                  <div className="text-3xl font-black text-gold">{g.total_qty}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{g.skus} SKUs</div>
                </div>
              );
            })}
          </div>

          {/* Detailed tables */}
          {GROUP_ORDER.map((key) => {
            const g = data[key];
            if (!g) return null;
            const isOpen = expanded[key];
            return (
              <div key={key} className="bg-navy-700 rounded-xl neon-border overflow-hidden">
                <button
                  onClick={() => toggle(key)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-navy-800/50 transition"
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gold" /> : <ChevronRight className="w-4 h-4 text-gold" />}
                    <span className="font-bold uppercase tracking-wide text-sm">{g.label}</span>
                    <span className="text-xs text-slate-400">{g.skus} SKUs</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-slate-400">Total Qty</span>
                    <span className="font-black text-gold text-lg">{g.total_qty}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gold/10">
                    <table className="w-full text-sm">
                      <thead className="bg-navy-800 text-[10px] uppercase tracking-[0.2em] text-gold/80">
                        <tr>
                          <th className="text-left px-5 py-2.5">Product Name</th>
                          <th className="text-right px-5 py-2.5">Qty in Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.length === 0 && (
                          <tr><td colSpan={2} className="px-5 py-4 text-slate-400 text-center">No items.</td></tr>
                        )}
                        {[...g.items].sort((a, b) => a.name.localeCompare(b.name)).map((item) => (
                          <tr key={item.id} className="border-t border-gold/10 hover:bg-navy-800/40">
                            <td className="px-5 py-3 font-medium">{item.name}</td>
                            <td className={`px-5 py-3 text-right font-bold ${item.stock === 0 ? "text-rose-400" : item.stock <= 3 ? "text-yellow-400" : "text-emerald-400"}`}>
                              {item.stock}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
